import { c as _c } from "react/compiler-runtime";
import { IcoPasskey40, IcoWarning40 } from '@onefootprint/icons';
import { HeaderTitle, NavigationHeader } from '@onefootprint/idv2';
import { getErrorMessage } from '@onefootprint/request';
import { UserChallengeActionKind } from '@onefootprint/types';
import type { PasskeyAttemptContext } from '@onefootprint/types/src/api/skip-liveness';
import { SkipLivenessClientType, SkipLivenessReason } from '@onefootprint/types/src/api/skip-liveness';
import { BottomSheet, Button, LinkButton } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLogger, trackAction } from '@/idv/utils';
import { useSkipLiveness } from '../../../../queries';
import { FPCustomEvents, sendCustomEvent } from '../../../../utils/custom-event';
import useLivenessMachine from '../../hooks/use-liveness-machine';
import useBiometricInit, { isRegisterPasskeyError } from '../../hooks/use-register-biometric';
const SUCCESS_TRANSITION_DELAY_MS = 2500;
const {
  logWarn,
  logInfo,
  logTrack
} = getLogger({
  location: 'passkeys-register'
});
const Register = t0 => {
  const $ = _c(83);
  const {
    actionKind
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "liveness.pages.register"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t1);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [state, send] = useLivenessMachine();
  const {
    idvContext: t2
  } = state.context;
  const {
    authToken
  } = t2;
  const biometricInitMutation = useBiometricInit();
  const skipLivenessMutation = useSkipLiveness();
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [passkeyRegisterAttempts, setPasskeyRegisterAttempts] = useState(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  useEffect(_temp, t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShowBottomSheet(true);
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const handleOpenBottomSheet = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setShowBottomSheet(false);
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const handleCloseBottomSheet = t6;
  let t7;
  if ($[5] !== actionKind || $[6] !== authToken || $[7] !== biometricInitMutation || $[8] !== passkeyRegisterAttempts || $[9] !== send) {
    t7 = () => {
      if (biometricInitMutation.isPending) {
        return;
      }
      biometricInitMutation.mutate({
        authToken,
        actionKind
      }, {
        onSuccess(t8) {
          const {
            deviceResponseJson
          } = t8;
          logTrack("Passkeys registration succeeded");
          setTimeout(() => {
            sendCustomEvent(FPCustomEvents.receivedDeviceResponseJson, {
              deviceResponseJson
            });
            send({
              type: "succeeded"
            });
          }, SUCCESS_TRANSITION_DELAY_MS);
        },
        onError(error) {
          let e = error;
          let elapsedTimeInOsPromptMs;
          if (isRegisterPasskeyError(error)) {
            e = error.error;
            elapsedTimeInOsPromptMs = error.elapsedTimeInOsPromptMs;
          }
          const errorMessage = getErrorMessage(e);
          logWarn(`Failed to register passkeys for user: ${errorMessage}`, e);
          const passkeyFailure = {
            errorMessage,
            elapsedTimeInOsPromptMs
          };
          setPasskeyRegisterAttempts([...passkeyRegisterAttempts, passkeyFailure]);
        }
      });
    };
    $[5] = actionKind;
    $[6] = authToken;
    $[7] = biometricInitMutation;
    $[8] = passkeyRegisterAttempts;
    $[9] = send;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleRegister = t7;
  let t8;
  if ($[11] !== authToken || $[12] !== passkeyRegisterAttempts || $[13] !== send || $[14] !== skipLivenessMutation) {
    t8 = () => {
      if (skipLivenessMutation.isPending) {
        return;
      }
      trackAction("passkeys:skipped");
      const context = {
        reason: SkipLivenessReason.failed,
        clientType: SkipLivenessClientType.web,
        numAttempts: passkeyRegisterAttempts.length,
        attempts: passkeyRegisterAttempts
      };
      skipLivenessMutation.mutate({
        authToken,
        context
      }, {
        onSuccess: () => {
          logInfo("Registered passkeys with success");
          send({
            type: "skipped"
          });
        },
        onError: _temp2
      });
    };
    $[11] = authToken;
    $[12] = passkeyRegisterAttempts;
    $[13] = send;
    $[14] = skipLivenessMutation;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleSkip = t8;
  let icon;
  let primaryButtonText;
  let secondaryButtonText;
  let headerTitle;
  let headerSubtitle;
  if (biometricInitMutation.isSuccess) {
    let t9;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <IcoPasskey40 color="primary" />;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    icon = t9;
    let t10;
    if ($[17] !== t) {
      t10 = t(_temp3);
      $[17] = t;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    headerTitle = t10;
    let t11;
    if ($[19] !== t) {
      t11 = t(_temp4);
      $[19] = t;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    headerSubtitle = t11;
  } else {
    if (passkeyRegisterAttempts.length) {
      let t9;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <IcoWarning40 color="error" />;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      icon = t9;
      let t10;
      if ($[22] !== t) {
        t10 = t(_temp5);
        $[22] = t;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      headerTitle = t10;
      let t11;
      if ($[24] !== t) {
        t11 = t(_temp6);
        $[24] = t;
        $[25] = t11;
      } else {
        t11 = $[25];
      }
      headerSubtitle = t11;
      let t12;
      if ($[26] !== t) {
        t12 = t(_temp7);
        $[26] = t;
        $[27] = t12;
      } else {
        t12 = $[27];
      }
      primaryButtonText = t12;
      let t13;
      if ($[28] !== t) {
        t13 = t(_temp8);
        $[28] = t;
        $[29] = t13;
      } else {
        t13 = $[29];
      }
      secondaryButtonText = t13;
    } else {
      let t9;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <IcoPasskey40 color="primary" />;
        $[30] = t9;
      } else {
        t9 = $[30];
      }
      icon = t9;
      let t10;
      if ($[31] !== actionKind || $[32] !== t) {
        t10 = actionKind === UserChallengeActionKind.replace ? t(_temp9) : t(_temp10);
        $[31] = actionKind;
        $[32] = t;
        $[33] = t10;
      } else {
        t10 = $[33];
      }
      headerTitle = t10;
      let t11;
      if ($[34] !== t) {
        t11 = t(_temp11);
        $[34] = t;
        $[35] = t11;
      } else {
        t11 = $[35];
      }
      headerSubtitle = t11;
      let t12;
      if ($[36] !== t) {
        t12 = t(_temp12);
        $[36] = t;
        $[37] = t12;
      } else {
        t12 = $[37];
      }
      primaryButtonText = t12;
    }
  }
  let t9;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <NavigationHeader />;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== icon) {
    t10 = <div className="flex items-center justify-center mb-2">{icon}</div>;
    $[39] = icon;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== headerSubtitle || $[42] !== headerTitle) {
    t11 = <HeaderTitle title={headerTitle} subtitle={headerSubtitle} />;
    $[41] = headerSubtitle;
    $[42] = headerTitle;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== biometricInitMutation.isPending || $[45] !== handleRegister || $[46] !== primaryButtonText || $[47] !== skipLivenessMutation.isPending) {
    t12 = primaryButtonText && <Button loading={biometricInitMutation.isPending} disabled={biometricInitMutation.isPending || skipLivenessMutation.isPending} onClick={handleRegister} fullWidth={true} size="large">{primaryButtonText}</Button>;
    $[44] = biometricInitMutation.isPending;
    $[45] = handleRegister;
    $[46] = primaryButtonText;
    $[47] = skipLivenessMutation.isPending;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== biometricInitMutation.isPending || $[50] !== handleSkip || $[51] !== secondaryButtonText || $[52] !== skipLivenessMutation.isPending) {
    t13 = secondaryButtonText && <Button loading={skipLivenessMutation.isPending} disabled={biometricInitMutation.isPending || skipLivenessMutation.isPending} onClick={handleSkip} fullWidth={true} size="large" variant="secondary">{secondaryButtonText}</Button>;
    $[49] = biometricInitMutation.isPending;
    $[50] = handleSkip;
    $[51] = secondaryButtonText;
    $[52] = skipLivenessMutation.isPending;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== biometricInitMutation.isSuccess || $[55] !== t) {
    t14 = !biometricInitMutation.isSuccess && <div className="flex items-center w-full justify-center pt-2"><LinkButton onClick={handleOpenBottomSheet}>{t(_temp13)}</LinkButton></div>;
    $[54] = biometricInitMutation.isSuccess;
    $[55] = t;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== t) {
    t15 = t(_temp14);
    $[57] = t;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== t) {
    t16 = t(_temp15);
    $[59] = t;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== t16) {
    t17 = <div className="text-body-2 text-primary whitespace-pre-wrap">{t16}</div>;
    $[61] = t16;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== t) {
    t18 = t(_temp16);
    $[63] = t;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== t18) {
    t19 = <div className="text-body-2 text-primary whitespace-pre-wrap">{t18}</div>;
    $[65] = t18;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== t17 || $[68] !== t19) {
    t20 = <div className="flex gap-2 flex-col mb-4">{t17}{t19}</div>;
    $[67] = t17;
    $[68] = t19;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== showBottomSheet || $[71] !== t15 || $[72] !== t20) {
    t21 = <BottomSheet open={showBottomSheet} onClose={handleCloseBottomSheet} title={t15} portalId="passkeys-register-container">{t20}</BottomSheet>;
    $[70] = showBottomSheet;
    $[71] = t15;
    $[72] = t20;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== t12 || $[75] !== t13 || $[76] !== t14 || $[77] !== t21) {
    t22 = <div className="flex flex-col mt-6 w-full gap-3">{t12}{t13}{t14}{t21}</div>;
    $[74] = t12;
    $[75] = t13;
    $[76] = t14;
    $[77] = t21;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  let t23;
  if ($[79] !== t10 || $[80] !== t11 || $[81] !== t22) {
    t23 = <div id="passkeys-register-container" className="flex flex-col items-center h-full justify-center text-center"><div>{t9}{t10}{t11}{t22}</div></div>;
    $[79] = t10;
    $[80] = t11;
    $[81] = t22;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  return t23;
};
export default Register;
function _temp() {
  logInfo("Passkeys registration started");
}
function _temp2(error_0) {
  logWarn(`Failed to skip liveness after retrying registering passkeys: ${getErrorMessage(error_0)}`, error_0);
}
function _temp3($) {
  return $.success.title;
}
function _temp4($_0) {
  return $_0.success.subtitle;
}
function _temp5($_1) {
  return $_1.retry.title;
}
function _temp6($_2) {
  return $_2.retry.subtitle;
}
function _temp7($_3) {
  return $_3.retry.cta;
}
function _temp8($_4) {
  return $_4.retry.skip;
}
function _temp9($_5) {
  return $_5["title-replace"];
}
function _temp10($_6) {
  return $_6["title-add"];
}
function _temp11($_7) {
  return $_7.subtitle;
}
function _temp12($_8) {
  return $_8.cta;
}
function _temp13($_9) {
  return $_9["learn-more"].cta;
}
function _temp14($_10) {
  return $_10["learn-more"].title;
}
function _temp15($_11) {
  return $_11["learn-more"]["content-1"];
}
function _temp16($_12) {
  return $_12["learn-more"]["content-2"];
}